import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { MotionConfig } from "framer-motion";
import {
  Check,
  CircleAlert,
  Mic,
  Clock,
  Eye,
  Lightbulb,
  CalendarDays,
  Video,
  Home,
  Bus,
  LockKeyhole,
  HeartPulse,
  Footprints,
  Phone,
  CreditCard,
  CircleDollarSign,
  TriangleAlert,
  MessageSquare,
  QrCode,
} from "lucide-react";
import type { Answer, LabAction, ParticipantView } from "../../shared/types.ts";
import {
  ConversationStep,
  type InputMode,
} from "./ParticipantConversation.tsx";
import {
  Eyebrow,
  ParticipantField,
  RichText,
  hasAnswer,
  sameAnswer,
} from "./ParticipantFields.tsx";
import { SaveContactCard } from "./SaveContactCard.tsx";
import { ParticipantIncentive } from "./ParticipantIncentive.tsx";
import { ParticipantHistory } from "./ParticipantHistory.tsx";
import { choiceAutoAdvances, useChoiceAdvance } from "./useChoiceAdvance.ts";
import { DOCK_TEXT_ACTION } from "../../../vendor/member-frontend/components/session-phases/AnswerDockParts.tsx";
import { ContinueButton } from "../../../vendor/member-core/components/ui/ContinueButton.tsx";
import { JourneyProgressRail } from "../../../vendor/member-core/components/JourneyProgressRail.tsx";
import { SectionProgressRing } from "../../../vendor/member-core/components/SectionProgressRing.tsx";
import {
  clusteredCompletion,
  deriveJourneyMarker,
} from "../../../vendor/member-core/lib/sectionProgress.ts";
import { Pill } from "../../../vendor/member-core/components/ui/Pill.tsx";
import { ExperienceOrientationScreen } from "../../../vendor/member-frontend/components/ExperienceOrientationScreen.tsx";
import { CompletedScreen } from "../../../vendor/member-frontend/components/CompletedScreen.tsx";
import type { Campaign } from "../../../vendor/member-core/types/session.ts";
import { answerError, draftAnswer } from "../../shared/answers.ts";
import "./participant.css";
import "./member-source.css";

export interface ParticipantExperienceProps {
  view: ParticipantView;
  onAction: (action: LabAction, controlRevision?: number) => Promise<void>;
  /** A mirror: another controller owns input, so unsent local text is discarded. */
  readOnly?: boolean;
  /**
   * Input is held for now (connection lost, or a projection behind the run). Nothing is
   * sent and controls are disabled, but unsent local text is kept for when input resumes.
   */
  paused?: boolean;
  embedded?: boolean;
  busy?: boolean;
}

type Frame = (
  content: ReactNode,
  dock?: ReactNode,
  centered?: boolean,
) => ReactNode;
interface StepProps {
  view: ParticipantView;
  readOnly: boolean;
  paused: boolean;
  /** The parent or this renderer has an action in flight. */
  waiting: boolean;
  /** This renderer has an action in flight. */
  pending: boolean;
  armed: boolean;
  frame: Frame;
  focusTarget: (element: HTMLElement | null) => void;
  advance: () => Promise<boolean>;
  respond: (variantId?: string) => void;
  send: (action: LabAction) => Promise<boolean>;
}

/** A new screen ignores activation briefly, so one double tap cannot skip it. */
const ARM_DELAY_MS = 300;
/** Focus follows a screen change only when this renderer requested it. */
const FOCUS_WINDOW_MS = 5000;

/**
 * Presents one participant session's projection. The server owns every domain
 * decision; this component dispatches typed actions and keeps only transient
 * presentation state (optimistic answers, pending and focus).
 */
export function ParticipantExperience({
  view,
  onAction,
  readOnly = false,
  paused = false,
  embedded = false,
  busy = false,
}: ParticipantExperienceProps) {
  const { step, session, progress } = view;
  const [pending, setPending] = useState<LabAction["type"] | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [armed, setArmed] = useState(false);
  const [requestedMode, setRequestedMode] = useState<{
    sessionId: string;
    mode: InputMode;
  } | null>(null);
  const inFlight = useRef(false),
    focusUntil = useRef(0);
  const focusRef = useRef<HTMLElement | null>(null),
    scrollRef = useRef<HTMLDivElement | null>(null);
  const waiting = busy || pending !== null;
  const advancing = pending === "session.advance";
  const mode =
    !readOnly && requestedMode?.sessionId === session.id
      ? requestedMode.mode
      : session.inputMode;
  // Sessions of the same story share step ids, so screen state is keyed by both.
  const stepKey = session.id + "\u0000" + step.id;
  const screen = stepKey + "\u0000" + session.phase;
  const history =
    step.historyScope === "chapter" &&
    (step.kind === "form" || step.kind === "conversation")
      ? view.history
      : undefined;
  const hasHistory = Boolean(history?.length);

  const focusTarget = useCallback((element: HTMLElement | null) => {
    focusRef.current = element;
  }, []);

  useLayoutEffect(() => {
    setFeedback(null);
    setArmed(false);
    const scroller = scrollRef.current,
      target = focusRef.current;
    if (!hasHistory && scroller && target && session.phase !== "prompt")
      reveal(scroller, target);
    else if (!hasHistory && scroller) scroller.scrollTop = 0;
    if (target && performance.now() < focusUntil.current) {
      focusUntil.current = 0;
      target.focus({ preventScroll: embedded });
    }
    const timer = window.setTimeout(() => setArmed(true), ARM_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [screen]);

  // Earlier exchanges stay available above the current task. Scroll only this
  // reading pane, including when a pending response replaces its loading state.
  useLayoutEffect(() => {
    const scroller = scrollRef.current,
      target = focusRef.current;
    if (!hasHistory || !scroller || !target) return;
    scroller.scrollTop +=
      target.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top -
      16;
  }, [screen, hasHistory, pending]);

  useEffect(() => {
    if (
      requestedMode &&
      (readOnly ||
        requestedMode.sessionId !== session.id ||
        requestedMode.mode === session.inputMode)
    )
      setRequestedMode(null);
  }, [requestedMode, readOnly, session.id, session.inputMode]);

  // Moving actions carry the screen they were taken on, so a stale or queued one is
  // rejected instead of moving the session past a step nobody saw.
  const send = useCallback(
    async (action: LabAction) => {
      const positioned =
        action.type === "session.advance" || action.type === "session.respond"
          ? { ...action, expectedStep: { id: step.id, phase: session.phase } }
          : action;
      try {
        await onAction(positioned, session.controller.revision);
        setFeedback(null);
        return true;
      } catch (error) {
        setFeedback(
          error instanceof Error && error.message
            ? error.message
            : "That did not go through. Please try again.",
        );
        return false;
      }
    },
    [onAction, session.controller.revision, step.id, session.phase],
  );

  const perform = useCallback(
    async (action: LabAction, movesScreen: boolean) => {
      if (readOnly || paused || inFlight.current) return false;
      inFlight.current = true;
      setPending(action.type);
      setFeedback(null);
      if (movesScreen) focusUntil.current = performance.now() + FOCUS_WINDOW_MS;
      try {
        const ok = await send(action);
        if (!ok) focusUntil.current = 0;
        return ok;
      } finally {
        inFlight.current = false;
        setPending(null);
      }
    },
    [readOnly, paused, send],
  );

  const advance = useCallback(
    () => perform({ type: "session.advance", sessionId: session.id }, true),
    [perform, session.id],
  );
  const respond = (variantId?: string) => {
    void perform(
      {
        type: "session.respond",
        sessionId: session.id,
        variantId: variantId ?? view.responseOptions?.[0]?.id ?? "baseline",
      },
      true,
    );
  };
  const changeMode = (next: InputMode) => {
    if (readOnly || paused || next === mode) return;
    setRequestedMode({ sessionId: session.id, mode: next });
    void perform(
      { type: "session.mode", sessionId: session.id, mode: next },
      false,
    ).then((ok) => {
      if (!ok) setRequestedMode(null);
    });
  };

  const frame: Frame = (content, dock, centered = false) => (
    <>
      <div className="pe-scroll" ref={scrollRef}>
        <div
          className="pe-stage"
          data-centered={(centered && !hasHistory) || undefined}
        >
          {hasHistory ? (
            <>
              <ParticipantHistory entries={history!} />
              <div className="pe-current-exchange">{content}</div>
            </>
          ) : (
            content
          )}
        </div>
      </div>
      {dock || feedback ? (
        <footer className="pe-dock">
          <div className="pe-dock__inner">
            {feedback && (
              <p className="pe-feedback" role="alert">
                <CircleAlert size={16} aria-hidden="true" />
                <span>{feedback}</span>
              </p>
            )}
            {dock}
          </div>
        </footer>
      ) : null}
    </>
  );

  const props: StepProps = {
    view,
    readOnly,
    paused,
    waiting,
    pending: advancing,
    armed,
    frame,
    focusTarget,
    advance,
    respond,
    send,
  };
  const conversation = () => (
    <ConversationStep
      key={stepKey}
      mediaIdentity={JSON.stringify([
        view.runId,
        session.id,
        session.publicationId,
        step.id,
        session.responses[step.id]?.variantId,
        session.responses[step.id]?.text,
        step.audio,
      ])}
      step={
        step.kind === "form"
          ? { ...step, heading: step.elaborationPrompt }
          : step
      }
      phase={session.phase}
      response={session.responses[step.id]}
      mode={mode}
      readOnly={readOnly}
      paused={paused}
      controlRevision={session.controller.revision}
      waiting={waiting}
      pending={pending === "session.respond"}
      armed={armed}
      focusTarget={focusTarget}
      frame={frame}
      responseOptions={view.responseOptions}
      onRespond={respond}
      onAdvance={() => {
        void advance();
      }}
      onMode={changeMode}
      continueButton={advanceButton(props)}
    />
  );
  let body: ReactNode;
  switch (step.kind) {
    case "landing":
      body = <LandingStep key={stepKey} {...props} />;
      break;
    case "form":
      body =
        step.elaborationPrompt && session.phase !== "prompt" ? (
          conversation()
        ) : (
          <FormStep
            key={stepKey}
            {...props}
            paused={paused || pending === "session.respond"}
          />
        );
      break;
    case "information":
      body = <InformationStep key={stepKey} {...props} />;
      break;
    case "transition":
      body = <TransitionStep key={stepKey} {...props} />;
      break;
    case "complete":
      body = <CompleteStep key={stepKey} {...props} />;
      break;
    case "conversation":
      body = conversation();
      break;
  }

  const segments = progress.sections ?? [];
  const showProgress = segments.length > 1;
  const marker = deriveJourneyMarker(segments, {
    isInSection: ["form", "conversation", "information"].includes(step.kind),
    complete: step.kind === "complete",
  });
  return (
    <MotionConfig reducedMotion="user">
      <div
        className="participant-experience"
        data-session={session.id}
        data-kind={
          step.elaborationPrompt && session.phase !== "prompt"
            ? "conversation"
            : step.kind
        }
        data-phase={session.phase}
        data-step={step.id}
        data-embedded={embedded || undefined}
        data-readonly={readOnly || undefined}
        data-paused={paused || undefined}
        aria-busy={waiting || undefined}
      >
        {showProgress ||
        readOnly ||
        view.incentive?.placement === "progress" ? (
          <header className="pe-chrome">
            <div className="pe-chrome__inner">
              <div className="pe-chrome__row">
                {readOnly && (
                  <span className="pe-mirror">
                    <Eye size={14} aria-hidden="true" />
                    Read-only mirror
                  </span>
                )}
              </div>
              {showProgress && (
                <JourneyProgressRail segments={segments} marker={marker} />
              )}
              {view.incentive?.placement === "progress" && (
                <ParticipantIncentive incentive={view.incentive} />
              )}
            </div>
          </header>
        ) : null}
        {body}
      </div>
    </MotionConfig>
  );
}

function reveal(scroller: HTMLElement, target: HTMLElement) {
  const view = scroller.getBoundingClientRect(),
    box = target.getBoundingClientRect();
  if (box.bottom > view.bottom)
    scroller.scrollTop += Math.min(
      box.top - view.top - 16,
      box.bottom - view.bottom + 16,
    );
}

interface PrimaryButtonProps {
  label: string;
  readOnly: boolean;
  waiting: boolean;
  pending: boolean;
  armed: boolean;
  /** Required input is missing. */
  blocked?: boolean;
  form?: string;
  onClick?: () => void;
}

function PrimaryButton({
  label,
  readOnly,
  waiting,
  pending,
  armed,
  blocked = false,
  form,
  onClick,
}: PrimaryButtonProps) {
  return (
    <div className="pe-primary-action" aria-busy={pending || undefined}>
      <ContinueButton
        label={label}
        delayMs={0}
        disabled={readOnly || waiting || blocked || !armed}
        onTap={() => {
          if (form)
            (
              document.getElementById(form) as HTMLFormElement | null
            )?.requestSubmit();
          else onClick?.();
        }}
      />
    </div>
  );
}

function advanceButton(p: StepProps, label = "Continue") {
  return (
    <PrimaryButton
      label={label}
      readOnly={p.readOnly || p.paused}
      waiting={p.waiting}
      pending={p.pending}
      armed={p.armed}
      onClick={() => {
        void p.advance();
      }}
    />
  );
}

function LandingStep(p: StepProps) {
  const { step } = p.view,
    scene = step.invitationScene;
  if (step.invitationOnly && scene)
    return p.frame(
      <section
        className="pe-invitation-scene"
        data-testid="participant-invitation-scene"
        aria-label="Campaign invitation"
      >
        <h2 className="pe-eyebrow" tabIndex={-1} ref={p.focusTarget}>
          {scene.kind === "sms" ? "Text message" : "Camera"}
        </h2>
        <InvitationScene scene={scene} />
      </section>,
      advanceButton(p, "Open the link"),
      true,
    );
  return p.frame(
    <div className="pe-landing">
      {scene && <InvitationScene scene={scene} />}
      <div className="pe-welcome-mark" aria-hidden="true">
        <Mic size={24} />
      </div>
      {step.logo && (
        <img
          className="pe-landing__logo"
          src={step.logo}
          alt={step.logoAlt ?? ""}
        />
      )}
      <h2
        className="pe-heading pe-heading--display"
        tabIndex={-1}
        ref={p.focusTarget}
      >
        <RichText text={step.heading ?? step.title} />
      </h2>
      {step.welcome?.planName && (
        <p className="pe-lead">
          Share your experience with <strong>{step.welcome.planName}</strong>
        </p>
      )}
      {step.body && (
        <p className="pe-lead">
          <RichText text={step.body} />
        </p>
      )}
      {step.italic && (
        <p className="pe-lead pe-lead--italic">
          <RichText text={step.italic} />
        </p>
      )}
      {step.privacyLabel && <Pill>{step.privacyLabel}</Pill>}
      {step.duration && (
        <p className="pe-duration">
          <Pill icon={<Clock size={14} aria-hidden="true" />}>
            {step.duration}
          </Pill>
        </p>
      )}
      {step.welcome?.incentiveAmount && (
        <p className="pe-lead">Earn up to {step.welcome.incentiveAmount}</p>
      )}
      {step.welcome?.topics?.length ? (
        <div className="pe-welcome-topics">
          <p className="pe-fineprint">Topics covered:</p>
          <ul>
            {step.welcome.topics.map((topic, i) => (
              <li key={i}>
                <Pill>{topic}</Pill>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {step.finePrint && (
        <p className="pe-fineprint">
          <RichText text={step.finePrint} />
        </p>
      )}
      {p.view.incentive?.placement === "landing" && (
        <ParticipantIncentive incentive={p.view.incentive} />
      )}
    </div>,
    advanceButton(p, step.ctaLabel),
  );
}

/** Source links describe the authored entry; only the domain CTA opens welcome. */
function InvitationScene({
  scene,
}: {
  scene: NonNullable<ParticipantView["step"]["invitationScene"]>;
}) {
  return (
    <div className="pe-invite" data-kind={scene.kind}>
      {scene.kind === "sms" ? (
        <>
          {scene.from && (
            <p className="pe-invite__from">
              <MessageSquare size={16} aria-hidden="true" />
              {scene.from}
            </p>
          )}
          <p className="pe-invite__bubble">
            {scene.text}
            <span className="pe-invite__link">{scene.link}</span>
          </p>
        </>
      ) : (
        <>
          <span className="pe-invite__qr" aria-hidden="true">
            <QrCode size={28} />
          </span>
          <span className="pe-invite__link">{scene.link}</span>
        </>
      )}
    </div>
  );
}

function FormStep({
  view,
  readOnly,
  paused,
  armed,
  frame,
  focusTarget,
  advance,
  respond,
  waiting,
  send,
}: StepProps) {
  const { step, session } = view,
    answers = session.answers;
  const formId = useId(),
    headingId = useId(),
    helperId = useId();
  // Drafts show committed choices before the server echoes them back.
  const [drafts, setDrafts] = useState<Record<string, Answer>>({});
  const [advancing, setAdvancing] = useState(false);
  const answersRef = useRef(answers),
    draftsRef = useRef(drafts);
  const typing = useRef(new Set<string>()),
    sending = useRef(new Map<string, Answer>());
  const chain = useRef(Promise.resolve()),
    failures = useRef(0),
    advancingRef = useRef(false);
  useLayoutEffect(() => {
    answersRef.current = answers;
    draftsRef.current = drafts;
  });

  const prune = useCallback((current: Record<string, Answer>) => {
    const settled = Object.keys(current).filter(
      (id) =>
        !typing.current.has(id) &&
        !sending.current.has(id) &&
        sameAnswer(current[id], answersRef.current[id]),
    );
    if (!settled.length) return current;
    const next = { ...current };
    for (const id of settled) delete next[id];
    return next;
  }, []);
  useEffect(() => {
    setDrafts(prune);
  }, [answers, prune]);
  // A mirror shows only the server's answers; a later handback starts from them. A takeover
  // missed while offline (a changed controller revision) counts too. A paused connection
  // alone keeps unsent text.
  const controlRevision = session.controller.revision,
    shownControl = useRef(controlRevision);
  useEffect(() => {
    const takenOver = shownControl.current !== controlRevision;
    shownControl.current = controlRevision;
    if (!readOnly && !takenOver) return;
    typing.current.clear();
    setDrafts((current) => (Object.keys(current).length ? {} : current));
  }, [readOnly, controlRevision]);

  // Answers are sent in order; a newer choice for the same field supersedes an unsent older one.
  const commit = (fieldId: string, value: Answer) => {
    if (readOnly || advancingRef.current) return Promise.resolve(false);
    value = draftAnswer(
      step.fields.find((f) => f.id === fieldId)!,
      value,
    );
    if (paused) {
      // Held locally like typed text; Continue sends it once input resumes.
      typing.current.add(fieldId);
      setDrafts((current) => ({ ...current, [fieldId]: value }));
      return Promise.resolve(false);
    }
    typing.current.delete(fieldId);
    setDrafts((current) => ({ ...current, [fieldId]: value }));
    if (
      !sending.current.has(fieldId) &&
      sameAnswer(answersRef.current[fieldId], value)
    ) {
      setDrafts(prune);
      return Promise.resolve(true);
    }
    sending.current.set(fieldId, value);
    const accepted = chain.current.then(async () => {
      if (sending.current.get(fieldId) !== value) return false;
      const ok = await send({
        type: "session.answer",
        sessionId: session.id,
        fieldId,
        value,
      });
      if (sending.current.get(fieldId) === value)
        sending.current.delete(fieldId);
      if (!ok) {
        failures.current++;
        setDrafts((current) => {
          if (current[fieldId] !== value) return current;
          const next = { ...current };
          delete next[fieldId];
          return next;
        });
      }
      setDrafts(prune);
      return ok;
    });
    chain.current = accepted.then(() => undefined);
    return accepted;
  };
  // Typed text stays local until blur or Continue; an untouched field never sends.
  const draft = (fieldId: string, value: string) => {
    if (readOnly || advancingRef.current) return;
    typing.current.add(fieldId);
    setDrafts((current) => ({ ...current, [fieldId]: value }));
  };

  const display = (fieldId: string): Answer | undefined =>
    readOnly || !(fieldId in drafts) ? answers[fieldId] : drafts[fieldId];
  const complete = step.fields.every(
    (field) =>
      (!field.required || hasAnswer(display(field.id))) &&
      !answerError(field, draftAnswer(field, display(field.id))),
  );
  const proceed = async () => {
    if (readOnly || paused || !complete || !armed || advancingRef.current)
      return false;
    for (const fieldId of [...typing.current]) {
      const value = draftsRef.current[fieldId];
      if (value !== undefined) commit(fieldId, value);
    }
    const failuresBefore = failures.current;
    advancingRef.current = true;
    setAdvancing(true);
    try {
      await chain.current;
      return failures.current === failuresBefore ? await advance() : false;
    } finally {
      advancingRef.current = false;
      setAdvancing(false);
    }
  };

  const autoChoice = choiceAutoAdvances(step);
  const choiceAdvance = useChoiceAdvance({
    disabled: readOnly || paused,
    controlRevision,
    advance: proceed,
  });
  // Like ShareVoice's SequentialView, revisiting an answered or optional field
  // keeps an explicit way forward. Only a fresh local selection hides it while
  // its acknowledgement, accepted answer, and advance are pending.
  const showContinue = !autoChoice || (complete && !choiceAdvance.pending);

  const heading = step.heading ?? step.title;
  const grouped = step.fields.length > 1;
  const headingNamesField =
    step.fields.length === 1 && step.fields[0].label === heading;
  const formMeter = clusteredCompletion(
    step.fields.map((field) => ({
      required: field.required,
      complete:
        hasAnswer(display(field.id)) &&
        !answerError(field, draftAnswer(field, display(field.id))),
    })),
  );
  const formProgressText = grouped
    ? `${formMeter.answered} of ${formMeter.total} answered`
    : `${view.progress.formCurrent ?? 1} of ${view.progress.formTotal ?? 1}`;
  const guidance = step.scaleContext?.guidance;
  const helped = Boolean(step.body || guidance);
  return frame(
    <form
      id={formId}
      className="pe-form"
      data-grouped={step.fields.length > 1 || undefined}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void proceed();
      }}
    >
      <div className="pe-form-progress">
        <span>{formProgressText}</span>
        <SectionProgressRing
          fraction={
            grouped
              ? formMeter.fraction
              : (view.progress.formCurrent ?? 1) /
                Math.max(1, view.progress.formTotal ?? 1)
          }
          ariaLabel="Section progress"
          ariaValueText={formProgressText}
        />
      </div>
      <Eyebrow step={step} />
      <h2 id={headingId} className="pe-heading" tabIndex={-1} ref={focusTarget}>
        <RichText text={heading} />
      </h2>
      {helped && (
        <div id={helperId} className="pe-helper">
          {step.body && (
            <p>
              <RichText text={step.body} />
            </p>
          )}
          {guidance && (
            <p>
              <RichText text={guidance} />
            </p>
          )}
        </div>
      )}
      <div className="pe-fields" data-grouped={grouped || undefined}>
        {step.fields.map((field) => (
          <div key={field.id} className="pe-field-row" data-field-id={field.id}>
            <ParticipantField
              field={field}
              resetKey={failures.current}
              value={display(field.id)}
              disabled={
                readOnly ||
                paused ||
                !armed ||
                advancing ||
                choiceAdvance.advancing
              }
              grouped={grouped}
              labelledBy={headingNamesField ? headingId : undefined}
              describedBy={helped ? helperId : undefined}
              lowLabel={step.scaleContext?.lowLabel}
              highLabel={step.scaleContext?.highLabel}
              onCommit={(value) => {
                if (field.kind === "text" && !typing.current.has(field.id))
                  return;
                const accepted = commit(field.id, value);
                if (autoChoice) choiceAdvance.select(accepted);
              }}
              onDraft={(value) => draft(field.id, value)}
            />
          </div>
        ))}
      </div>
    </form>,
    showContinue || step.elaborationPrompt ? (
      <>
        {step.elaborationPrompt && (
          <button
            type="button"
            className={DOCK_TEXT_ACTION}
            disabled={
              readOnly ||
              paused ||
              waiting ||
              advancing ||
              !armed ||
              !complete ||
              !view.responseOptions?.length
            }
            onClick={() => respond()}
          >
            {step.elaborationPrompt}
          </button>
        )}
        {showContinue && (
          <PrimaryButton
            form={formId}
            label={step.ctaLabel ?? "Continue"}
            readOnly={readOnly || paused}
            waiting={advancing}
            pending={advancing}
            armed={armed}
            blocked={!complete}
          />
        )}
      </>
    ) : undefined,
  );
}

const tipIcons = {
  calendar: CalendarDays,
  video: Video,
  home: Home,
  van: Bus,
  lock: LockKeyhole,
  heart: HeartPulse,
  steps: Footprints,
  phone: Phone,
  alert: TriangleAlert,
  card: CreditCard,
  cost: CircleDollarSign,
};

function InformationStep(p: StepProps) {
  const { step } = p.view,
    tip = step.tip;
  const heading = step.heading ?? tip?.title ?? step.title;
  const Icon = tipIcons[tip?.icon as keyof typeof tipIcons] ?? Lightbulb;
  const closing =
    step.informationKind === "closing" || Boolean(step.closingList);
  const [first, ...rest] = heading.split(/(?<=\.)\s+/);
  return p.frame(
    <article className="pe-info">
      {tip?.stepLabel && <p className="pe-eyebrow">{tip.stepLabel}</p>}
      {tip ? (
        <div className="pe-tip">
          <span className="pe-tip__icon" aria-hidden="true">
            <Icon size={22} />
          </span>
          <h2 className="pe-heading" tabIndex={-1} ref={p.focusTarget}>
            <RichText text={tip.title ?? heading} />
          </h2>
          {tip.body && (
            <p className="pe-tip__body">
              <RichText text={tip.body} />
            </p>
          )}
          {tip.bullets?.length ? (
            <ul className="pe-tip__bullets">
              {tip.bullets.map((bullet, index) => (
                <li key={index}>
                  <RichText text={bullet} />
                </li>
              ))}
            </ul>
          ) : null}
          {tip.source && (
            <p className="pe-tip__meta">
              <RichText text={tip.source} />
            </p>
          )}
          {tip.contact && <p className="pe-tip__meta">{tip.contact}</p>}
          {tip.url && <p className="pe-tip__meta">{tip.url}</p>}
        </div>
      ) : closing ? (
        <div className="pe-closing-heading" tabIndex={-1} ref={p.focusTarget}>
          <span className="pe-welcome-mark" aria-hidden="true">
            <Check size={24} />
          </span>
          <h2 className="pe-heading">
            <RichText text={first} />
          </h2>
          {rest.length > 0 && (
            <p className="pe-lead">
              <RichText text={rest.join(" ")} />
            </p>
          )}
        </div>
      ) : (
        <h2 className="pe-message" tabIndex={-1} ref={p.focusTarget}>
          <RichText text={heading} />
        </h2>
      )}
      {step.body && (
        <p className="pe-lead">
          <RichText text={step.body} />
        </p>
      )}
      {tip?.personalNote && (
        <aside className="pe-personal-note">
          <p className="pe-eyebrow">{tip.personalNote.label}</p>
          <p>
            <RichText text={tip.personalNote.text} />
          </p>
        </aside>
      )}
      {step.saveCard && (
        <SaveContactCard card={step.saveCard} disabled={p.readOnly} />
      )}
      {step.closingList?.length ? (
        <ol className="pe-closing">
          {step.closingList.map((item, index) => (
            <li key={index}>
              <span className="pe-closing__number">{index + 1}</span>
              <span>
                <RichText text={item} />
              </span>
            </li>
          ))}
        </ol>
      ) : null}
      {(tip?.total ?? 0) > 1 && (
        <div
          className="pe-tip-dots"
          role="img"
          aria-label={`${(tip?.index ?? 0) + 1} of ${tip!.total}`}
        >
          {Array.from({ length: tip!.total! }, (_, index) => (
            <span
              key={index}
              data-current={index === tip?.index || undefined}
            />
          ))}
        </div>
      )}
      {tip?.footer && (
        <p className="pe-tip-footer">
          <RichText text={tip.footer} />
        </p>
      )}
    </article>,
    advanceButton(p, step.ctaLabel),
    !tip && !step.saveCard && !step.closingList?.length,
  );
}

function TransitionStep(p: StepProps) {
  const { step } = p.view;
  return p.frame(
    <div tabIndex={-1} ref={p.focusTarget}>
      <ExperienceOrientationScreen
        title={(step.heading ?? step.title).replaceAll("__", "")}
        body={step.body?.replaceAll("__", "")}
        eyebrow={step.eyebrow ?? null}
        ctaLabel={step.ctaLabel}
        disabled={p.readOnly || p.paused || p.waiting || !p.armed}
        onContinue={() => {
          void p.advance();
        }}
      />
    </div>,
    undefined,
    true,
  );
}

/** The source completion accepts campaign HTML. Encode authored plain text first. */
const completionHtml = (text: string) =>
  text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replace(/__(.+?)__/g, "<u>$1</u>")
    .replaceAll("\n", "<br>");

function CompleteStep({ view, frame, focusTarget }: StepProps) {
  const { step } = view;
  // This portable screen reads only Campaign.wrapup. Keep the lab's projection
  // at that boundary, rather than constructing an unrelated app campaign.
  const completion = {
    wrapup: {
      headline: "",
      body: "",
      open_ended_prompt: "",
      completion_headline: completionHtml(step.heading ?? step.title),
      completion_body: completionHtml(step.body ?? ""),
    },
  } satisfies Pick<Campaign, "wrapup">;
  return frame(
    <div tabIndex={-1} ref={focusTarget}>
      {view.incentive?.placement === "complete" && (
        <ParticipantIncentive incentive={view.incentive} />
      )}
      <CompletedScreen
        campaign={completion as Campaign}
        completionContext="just_completed"
      />
    </div>,
    null,
    true,
  );
}
